import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, Briefcase, FileText, Pencil, Settings, User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { checkImageFile } from '@/components/ui/ImageUpload';
import { LoadingBlock } from '@/components/ui/Loader';
import { QueryError } from '@/components/ui/QueryError';
import { ROLE_LABEL } from '@/constants/roles';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { teacherApi } from '../api/teacher.api';
import { EditProfileModal } from '../components/profile/EditProfileModal';
import { summariseTeaching, tenureSince } from '../components/profile/profileSummary';
import { TeacherProfileHeroCard } from '../components/profile/TeacherProfileHeroCard';
import { TeacherPersonalInfoTab } from '../components/profile/TeacherPersonalInfoTab';
import { TeacherProfessionalDetailsTab } from '../components/profile/TeacherProfessionalDetailsTab';
import { TeacherSubjectsTab } from '../components/profile/TeacherSubjectsTab';
import { TeacherDocumentsTab } from '../components/profile/TeacherDocumentsTab';
import { TeacherSettingsTab } from '../components/profile/TeacherSettingsTab';

export interface TeacherProfilePageProps {
  initialTab?: 'personal' | 'professional' | 'subjects' | 'documents' | 'settings';
}

type TabKey = 'personal' | 'professional' | 'subjects' | 'documents' | 'settings';

export function TeacherProfilePage({ initialTab }: TeacherProfilePageProps) {
  const location = useLocation();

  const getInitialActiveTab = (): TabKey => {
    if (initialTab) return initialTab;
    if (location.pathname.includes('/teacher/settings')) return 'settings';
    return 'personal';
  };

  const [activeTab, setActiveTab] = useState<TabKey>(getInitialActiveTab);

  /*
    Their own record and their own allotments, read together: the banner's
    figures and three of the tabs need both. Both endpoints answer only for the
    caller, from the token — there is no id here to point at anybody else.
  */
  const { data, isLoading, error, refetch } = useApiQuery(
    () => Promise.all([teacherApi.myProfile(), teacherApi.myAllocations()]),
    [],
  );
  const profile = data?.[0] ?? null;
  const teaching = useMemo(() => summariseTeaching(data?.[1] ?? []), [data]);

  const [isEditing, setIsEditing] = useState(false);
  const [isPhotoBusy, setIsPhotoBusy] = useState(false);
  const [isConfirmingPhotoRemoval, setIsConfirmingPhotoRemoval] = useState(false);
  /** Changes after every upload, so the browser does not keep the old face. */
  const [photoVersion, setPhotoVersion] = useState(0);

  /** Same limits as the office's staff photo; the server checks them again. */
  async function uploadPhoto(file: File) {
    const problem = checkImageFile(file, {
      accept: 'image/png,image/jpeg,image/webp',
      maxSizeMb: 2,
    });
    if (problem) {
      toast.error('Could not use that photograph', problem);
      return;
    }
    setIsPhotoBusy(true);
    try {
      await teacherApi.uploadMyPhoto(file);
      setPhotoVersion(Date.now());
      toast.success('Photograph updated');
      refetch();
    } catch (caught) {
      toast.error(
        'Could not upload the photograph',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsPhotoBusy(false);
    }
  }

  async function removePhoto() {
    setIsPhotoBusy(true);
    try {
      await teacherApi.removeMyPhoto();
      setIsConfirmingPhotoRemoval(false);
      toast.success('Photograph removed');
      refetch();
    } catch (caught) {
      toast.error(
        'Could not remove the photograph',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsPhotoBusy(false);
    }
  }

  useEffect(() => {
    if (location.pathname.includes('/teacher/settings')) {
      setActiveTab('settings');
    } else if (location.pathname.includes('/teacher/profile')) {
      if (initialTab) {
        setActiveTab(initialTab);
      }
    }
  }, [location.pathname, initialTab]);

  return (
    <div className="flex flex-col gap-5">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black tracking-tight text-ink sm:text-2xl">My Profile</h1>
          <p className="mt-0.5 text-sm font-medium text-ink-2">
            Your personal and professional details
          </p>
        </div>
        {profile?.details && (
          <Button
            size="lg"
            className="font-bold text-[14px]"
            leftIcon={<Pencil className="h-4 w-4" />}
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </Button>
        )}
      </div>

      {isLoading && <LoadingBlock label="Loading your profile" />}
      {!isLoading && (error || !profile) && (
        <QueryError
          title="Could not load your profile"
          error={error ?? { message: 'Your profile did not come back. Please try again.' }}
          onRetry={refetch}
        />
      )}

      {profile && (
        <>
          {/* Hero Profile Banner Card */}
          <TeacherProfileHeroCard
            name={profile.fullName}
            photo={{
              hasPhoto: profile.hasPhoto,
              path: teacherApi.myPhotoPath(),
              reloadKey: photoVersion,
            }}
            isPhotoBusy={isPhotoBusy}
            onPickPhoto={(file) => void uploadPhoto(file)}
            onRemovePhoto={() => setIsConfirmingPhotoRemoval(true)}
            title={profile.designation ?? profile.roleName ?? ROLE_LABEL[profile.staffType]}
            isActive
            classes={teaching.sections.length}
            students={teaching.students}
            subjects={teaching.subjects.length}
            tenure={tenureSince(profile.joinedOn)}
          />

          {/* Tabs Navigation Bar */}
          <div className="flex items-center gap-6 md:gap-8 border-b border-line overflow-x-auto no-scrollbar pt-2">
            <button
              type="button"
              onClick={() => setActiveTab('personal')}
              className={cn(
                'flex items-center gap-2.5 pb-4 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
                activeTab === 'personal'
                  ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
                  : 'text-ink-3 hover:text-ink',
              )}
            >
              <User className="w-5 h-5" />
              <span>Personal Information</span>
              {activeTab === 'personal' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('professional')}
              className={cn(
                'flex items-center gap-2.5 pb-4 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
                activeTab === 'professional'
                  ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
                  : 'text-ink-3 hover:text-ink',
              )}
            >
              <Briefcase className="w-5 h-5" />
              <span>Professional Details</span>
              {activeTab === 'professional' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('subjects')}
              className={cn(
                'flex items-center gap-2.5 pb-4 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
                activeTab === 'subjects'
                  ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
                  : 'text-ink-3 hover:text-ink',
              )}
            >
              <BookOpen className="w-5 h-5" />
              <span>Subjects & Classes</span>
              {activeTab === 'subjects' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className={cn(
                'flex items-center gap-2.5 pb-4 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
                activeTab === 'documents'
                  ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
                  : 'text-ink-3 hover:text-ink',
              )}
            >
              <FileText className="w-5 h-5" />
              <span>Documents</span>
              {activeTab === 'documents' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={cn(
                'flex items-center gap-2.5 pb-4 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
                activeTab === 'settings'
                  ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
                  : 'text-ink-3 hover:text-ink',
              )}
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
              {activeTab === 'settings' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
              )}
            </button>
          </div>

          {/* Tab Content Panes */}
          {activeTab === 'personal' && (
            <TeacherPersonalInfoTab
              profile={profile}
              sections={teaching.sections}
              subjects={teaching.subjects}
            />
          )}
          {activeTab === 'professional' && <TeacherProfessionalDetailsTab profile={profile} />}
          {activeTab === 'subjects' && <TeacherSubjectsTab sections={teaching.sections} />}
          {activeTab === 'documents' && <TeacherDocumentsTab />}
          {activeTab === 'settings' && <TeacherSettingsTab />}
        </>
      )}

      {profile && (
        <EditProfileModal
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          profile={profile}
          onSaved={() => {
            setIsEditing(false);
            toast.success('Profile saved');
            // The banner and every tab read the same query, so one re-read keeps
            // the whole page in step with what was saved.
            refetch();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={isConfirmingPhotoRemoval}
        tone="danger"
        title="Remove your photograph?"
        description="Your initials are shown in its place until you add another."
        confirmLabel="Remove"
        isLoading={isPhotoBusy}
        onConfirm={() => void removePhoto()}
        onCancel={() => setIsConfirmingPhotoRemoval(false)}
      />
    </div>
  );
}
